/**
 * Cart API Service
 *
 * @package Enterprise\BookingSystem\Services
 */

/**
 * Internal dependencies
 */
import type { Cart } from "../../types/cart.types";

import { BaseApiService } from "./base-api.service";

export type ServiceAreaResult =
	| { serviceable: false; message: string }
	| { serviceable: true; label: string; tax_rate: number; delivery_fee: number };

export type AddToCartCommand = {
	product_id: number;
	quantity: number;
	variant_id?: null | number;
};

export type UpdateEventDetailsCommand = {
	city: string;
	event_date: string;
	state: string;
};

export type RemoveFromCartCommand = {
	cart_item_id: number;
};

export type UpdateCartItemCommand = {
	item_id: number;
	quantity: number;
};

export class CartApiService extends BaseApiService {
	/**
	 * Get current cart
	 */
	async getCart(): Promise<Cart> {
		return this.get<Cart>("/cart");
	}

	/**
	 * Add item to cart
	 */
	async addItem(command: AddToCartCommand): Promise<Cart> {
		const { variant_id, ...rest } = command;
		const body = variant_id == null ? rest : { ...rest, variant_id };
		return this.post<Cart>("/cart/add", body);
	}

	/**
	 * Remove item from cart
	 */
	async removeItem(command: RemoveFromCartCommand): Promise<Cart> {
		return this.post<Cart>("/cart/remove", command);
	}

	/**
	 * Update cart item quantity
	 */
	async updateItemQuantity(command: UpdateCartItemCommand): Promise<Cart> {
		return this.post<Cart>("/cart/update", command);
	}

	/**
	 * Update order-level event details (event date, state, city)
	 */
	async updateEventDetails(command: UpdateEventDetailsCommand): Promise<Cart> {
		return this.post<Cart>("/cart/event-details", command);
	}

	/**
	 * Clear cart
	 */
	async clearCart(): Promise<void> {
		await this.post("/cart/clear", {});
	}

	/**
	 * Check whether a ZIP code falls within a configured service area.
	 * Uses BaseApiService so the wp_rest nonce is included automatically.
	 */
	async checkServiceArea(zip: string): Promise<ServiceAreaResult> {
		return this.get<ServiceAreaResult>(
			`/service-areas/check?zip=${encodeURIComponent(zip)}`,
		);
	}
}

// Singleton instance
export const cartApiService = new CartApiService();
